/*
 * Level introduction screen — FR-INTRO-01 + FR-INTRO-02.
 *
 * Renders ONE rune at a time from the level's new-rune set:
 *   - the focal rune glyph as a hero card,
 *   - its Latin equivalent,
 *   - an example word (when one matches the AC rules) with the focal rune
 *     highlighted in context and the English translation.
 *
 * The current index is read from `params.idx` (default 0). FR-INTRO-02
 * adds the step-through controls (Previous / Skip / Next) that drive idx
 * via the route param — each press calls `navigate()` rather than
 * mutating internal state, so refresh/back behave correctly. Previous is
 * rendered only when idx > 0 (rather than disabled at idx 0), and the
 * Next button relabels to "Start exercises" on the last rune, kicking
 * off the exercise session via the `exercise-a` route.
 *
 * The "5 runes per level" comes from getRunesForLevel(). The placeholder
 * corpus (public/content/runes.csv) is sparse: at L1 there's only one
 * type=word entry. The screen treats the available set as authoritative
 * — when Roy's real CSV lands (#C-1) it will provide a proper 5-per-level
 * set without any code change here.
 */

import { navigate } from '../router';
import { getCorpus } from '../data/csv';
import { getRunesForLevel, findExampleWord } from '../data/level-runes';

export function renderLevelIntro(mount: HTMLElement, params?: Record<string, string>): void {
  const level = parseLevel(params?.['n']);
  const idx = parseIdx(params?.['idx']);

  const section = document.createElement('section');
  section.dataset.testid = 'level-intro';
  section.setAttribute('aria-labelledby', 'level-intro-heading');

  if (level === null) {
    section.appendChild(renderUnavailable('Pick a level from the home screen to begin.'));
    section.appendChild(renderExitButton());
    mount.appendChild(section);
    return;
  }

  section.dataset.level = String(level);

  const runes = getRunesForLevel(getCorpus(), level);
  const focalEntry = runes[idx];

  if (!focalEntry) {
    // Don't append the "Meet the new runes" heading here — renderUnavailable
    // creates its own <h1 id="level-intro-heading">, and we'd collide with it.
    // The unavailable message already names the level, so the level eyebrow
    // would be redundant too.
    section.appendChild(
      renderUnavailable(
        runes.length === 0
          ? `No runes are configured for Level ${level} yet.`
          : `That rune is out of range for Level ${level}.`,
      ),
    );
    section.appendChild(renderExitButton());
    mount.appendChild(section);
    return;
  }

  // Success path: append the level eyebrow + heading after we know we have a
  // focal entry. This keeps the id="level-intro-heading" attribute unique in
  // the rendered DOM regardless of which branch ran.
  const eyebrow = document.createElement('span');
  eyebrow.className = 'tag tag--eyebrow';
  eyebrow.textContent = `Level ${level}`;
  section.appendChild(eyebrow);

  const heading = document.createElement('h1');
  heading.id = 'level-intro-heading';
  heading.style.marginTop = 'var(--space-4)';
  heading.textContent = 'Meet the new runes';
  section.appendChild(heading);

  section.dataset.idx = String(idx);
  section.appendChild(renderFocalCard(focalEntry));

  const example = findExampleWord(getCorpus(), focalEntry, level);
  if (example) {
    section.appendChild(renderExampleCard(example, focalEntry));
  }

  section.appendChild(renderControls(level, idx, runes.length));
  section.appendChild(renderExitButton());
  mount.appendChild(section);
}

function parseLevel(raw: string | undefined): number | null {
  if (raw === undefined) return null;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1 || parsed > 8) return null;
  return parsed;
}

function parseIdx(raw: string | undefined): number {
  if (raw === undefined) return 0;
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 0) return 0;
  return parsed;
}

function renderFocalCard(entry: RuneEntryLike): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card card--elevated card--hero';
  card.dataset.testid = 'focal-card';
  card.style.marginTop = 'var(--space-6)';
  card.style.textAlign = 'center';

  // Focal glyph = the first rune in the entry's rune field. Use Array.from
  // so supplementary-plane glyphs (when the bespoke font lands) survive.
  const focalGlyph = Array.from(entry.rune)[0] ?? entry.rune;

  const glyph = document.createElement('div');
  glyph.className = 'rune rune--hero';
  glyph.dataset.testid = 'focal-rune';
  glyph.textContent = focalGlyph;
  card.appendChild(glyph);

  const latin = document.createElement('div');
  latin.dataset.testid = 'focal-latin';
  latin.style.marginTop = 'var(--space-4)';
  latin.style.fontSize = 'var(--font-size-body-l)';
  latin.style.fontWeight = 'var(--font-weight-semibold)';
  latin.textContent = entry.latin;
  card.appendChild(latin);

  return card;
}

function renderExampleCard(example: RuneEntryLike, focal: RuneEntryLike): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';
  card.dataset.testid = 'example-card';
  card.style.marginTop = 'var(--space-5)';

  const eyebrow = document.createElement('span');
  eyebrow.className = 'tag';
  eyebrow.textContent = 'Example';
  card.appendChild(eyebrow);

  const latin = document.createElement('div');
  latin.dataset.testid = 'example-latin';
  latin.style.marginTop = 'var(--space-3)';
  latin.style.fontSize = 'var(--font-size-body-l)';
  latin.style.fontWeight = 'var(--font-weight-semibold)';
  latin.textContent = example.latin;
  card.appendChild(latin);

  const runes = renderHighlightedRune(example.rune, focal.rune);
  runes.dataset.testid = 'example-rune';
  runes.style.marginTop = 'var(--space-3)';
  runes.style.fontSize = 'var(--font-size-display-s)';
  runes.style.letterSpacing = '0.1em';
  card.appendChild(runes);

  const english = document.createElement('div');
  english.dataset.testid = 'example-english';
  english.style.marginTop = 'var(--space-3)';
  english.style.color = 'var(--color-text-warm)';
  english.textContent = example.english;
  card.appendChild(english);

  return card;
}

/**
 * Render the example word's rune sequence with every occurrence of the
 * focal rune wrapped in a `<mark>` tinted with the terracotta accent.
 * Uses Array.from so multi-codepoint glyphs survive intact.
 */
function renderHighlightedRune(runeString: string, focalRuneField: string): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'rune';

  const focalGlyph = Array.from(focalRuneField)[0] ?? focalRuneField;

  for (const ch of Array.from(runeString)) {
    if (ch === focalGlyph) {
      const mark = document.createElement('mark');
      mark.dataset.testid = 'focal-highlight';
      mark.style.background = 'transparent';
      mark.style.color = 'var(--color-terracotta)';
      mark.style.fontWeight = 'var(--font-weight-bold)';
      mark.textContent = ch;
      wrapper.appendChild(mark);
    } else {
      // Wrap non-focal glyphs in spans (rather than text nodes) so the
      // DOM is predictable for tests and so future styling can target
      // them without parsing text nodes.
      const span = document.createElement('span');
      span.textContent = ch;
      wrapper.appendChild(span);
    }
  }

  return wrapper;
}

function renderUnavailable(message: string): HTMLElement {
  const card = document.createElement('article');
  card.className = 'card';
  card.dataset.testid = 'level-intro-unavailable';
  card.style.marginTop = 'var(--space-5)';

  const heading = document.createElement('h1');
  heading.id = 'level-intro-heading';
  heading.textContent = 'Nothing to introduce';
  card.appendChild(heading);

  const body = document.createElement('p');
  body.style.marginTop = 'var(--space-3)';
  body.style.color = 'var(--color-text-warm)';
  body.textContent = message;
  card.appendChild(body);

  return card;
}

/**
 * Step-through controls — FR-INTRO-02.
 *
 * DOM order: Previous (when not first) → Skip → Next. Visually they sit
 * in a single flex row; the primary action (Next/Start exercises) is
 * rightmost so it reads as the forward step. Previous is omitted at the
 * first rune rather than rendered disabled — simpler DOM, and the AC
 * only requires that Previous stay within the current intro phase.
 *
 * The Next button's label and target both flip on the last rune: instead
 * of advancing the idx, it kicks off the exercise session by navigating
 * to `exercise-a?n={level}`. Skip routes to the same place; it's always
 * present so the user can bail out of the intro at any rune.
 */
function renderControls(level: number, idx: number, count: number): HTMLElement {
  const isFirst = idx === 0;
  const isLast = idx === count - 1;

  const row = document.createElement('div');
  row.dataset.testid = 'level-intro-controls';
  row.style.display = 'flex';
  row.style.gap = 'var(--space-3)';
  row.style.marginTop = 'var(--space-6)';

  if (!isFirst) {
    const prev = document.createElement('button');
    prev.type = 'button';
    prev.className = 'btn btn--secondary';
    prev.dataset.testid = 'level-intro-prev';
    prev.textContent = '← Previous';
    prev.addEventListener('click', () => {
      navigate(`level-intro?n=${level}&idx=${idx - 1}`);
    });
    row.appendChild(prev);
  }

  const skip = document.createElement('button');
  skip.type = 'button';
  skip.className = 'btn btn--ghost';
  skip.dataset.testid = 'level-intro-skip';
  skip.textContent = 'Skip to exercises';
  skip.addEventListener('click', () => {
    navigate(`exercise-a?n=${level}`);
  });
  row.appendChild(skip);

  const next = document.createElement('button');
  next.type = 'button';
  next.className = 'btn btn--primary';
  next.dataset.testid = 'level-intro-next';
  next.textContent = isLast ? 'Start exercises →' : 'Next →';
  next.addEventListener('click', () => {
    if (isLast) {
      navigate(`exercise-a?n=${level}`);
    } else {
      navigate(`level-intro?n=${level}&idx=${idx + 1}`);
    }
  });
  row.appendChild(next);

  return row;
}

function renderExitButton(): HTMLButtonElement {
  // FR-NAV-02 exit affordance: users can always escape back to the level
  // grid, regardless of how far they've stepped into the intro phase.
  const back = document.createElement('button');
  back.type = 'button';
  back.className = 'btn btn--secondary';
  back.dataset.testid = 'level-intro-exit';
  back.style.marginTop = 'var(--space-6)';
  back.textContent = 'Back to levels';
  back.addEventListener('click', () => {
    navigate('');
  });
  return back;
}

// Local structural alias so the helper signatures don't have to import
// the full RuneEntry type just for `.latin`, `.rune`, `.english`.
interface RuneEntryLike {
  latin: string;
  rune: string;
  english: string;
}
